import React, { useState, useEffect } from 'react';
import { Wand2 } from 'lucide-react';
import VoiceSelector from './VoiceSelector';
import LanguageSelector from './LanguageSelector';
import FlowAdjuster from './FlowAdjuster';
import PaceAdjuster from './PaceAdjuster';
import TransliterationTextBox from './TransliterationTextBox';
import Tooltip from "./Tooltip";
// PostProcessingCheckbox temporarily removed
import { logEnhancedGeneration } from '../utils/enhancedAnalytics';
import { checkAndUpdateQuota, getQuotaStatus } from '../utils/quotaManager';
import { REQUEST_CONFIG } from '../config/constants';

const PRELOADED_TEXTS = [
  "భూమి నుండి చూసినప్పుడు, చంద్రుడు మరియు సూర్యుడు చాలా సమానమైన పరిమాణాన్ని కలిగి ఉన్నట్లు కనిపిస్తారు. ఇందుకే సూర్యగ్రహణం సమయంలో చంద్రుడు సూర్యుడిని దాదాపు సరిగ్గా కప్పివేస్తాడు.",
  "ఆంధ్రప్రదేశ్ మరియు తెలంగాణలో పెరిగే  కరివేపాకు, దాని ఔషధ గుణాల కారణంగా ఆరోగ్యానికి చాలా ప్రయోజనకరమైనదిగా పరిగణించబడుతుంది, ఇది జీర్ణశక్తిని మెరుగుపరుస్తుంది మరియు మధుమేహాన్ని నియంత్రించడంలో సహాయపడుతుంది.",
  "లేపాక్షి వేణుగోపాల స్వామి ఆలయంలో ఆధారం లేని స్తంభం ఉంది, దీనిని వేలాడే స్తంభం అంటారు, ఇది భూమిని తాకదు మరియు ఒక సన్నని కాగితాన్ని దాని కింద నుండి దూర్చవచ్చు."
];

const GenerateSpeech = ({ 
  user, 
  voices,
  setQuotaStatus,
  onAudioGenerated,
  selectedVoice = null, 
  setSelectedVoice = null 
}) => {
  // Local state
  const [text, setText] = useState(PRELOADED_TEXTS[Math.floor(Math.random() * PRELOADED_TEXTS.length)]);
  const [voice, setVoice] = useState(selectedVoice || 266);
  const [isGenerating, setIsGenerating] = useState(false);
  const [flowValue, setFlowValue] = useState(10); // Default is now 9
  const [paceValue, setPaceValue] = useState(2); // Default pace of 2
  // Post-processing checkbox removed, but keeping the value as true for backend compatibility
  const postProcessing = true;

  // Update local voice when selected voice changes from parent
  useEffect(() => {
    if (selectedVoice) {
      setVoice(selectedVoice);
    }
  }, [selectedVoice]);

  // Update parent when voice changes locally
  useEffect(() => {
    if (setSelectedVoice && voice !== selectedVoice) {
      setSelectedVoice(voice);
    }
  }, [voice, selectedVoice, setSelectedVoice]);

  const handleTextChange = (newText) => {
    setText(newText);
  };

  const handleGenerate = async () => {
    if (!text || !voice || isGenerating) return;
  
    // Check if text length exceeds the max allowed characters per request
    if (text.length > REQUEST_CONFIG.MAX_CHARAS_PER_REQUEST) {
      alert(`Your text is too long. Please remove ${text.length - REQUEST_CONFIG.MAX_CHARAS_PER_REQUEST} characters and try again.`);
      return;
    }
  
    // Add quota check before proceeding
    try {
      const quotaCheck = await checkAndUpdateQuota(user.uid, text.length);
      if (!quotaCheck.canProceed) {
        alert(`Quota exceeded! You have ${quotaCheck.remainingChars.toLocaleString()} characters remaining.`);
        return;
      }
  
      setIsGenerating(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);
  
      const response = await fetch(
        "https://consumelucidapp.azurewebsites.net/api/HttpTrigger25?code=_b5Sa1FqjSP6tubEAB_0U2wBx2OM2-89wbZEbyfu9PynAzFu_mbMBQ==",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            input_text: text,
            voice_id: voice,
            flow: flowValue,
            pace: paceValue,
            post_processing: postProcessing
          }),
          signal: controller.signal
        }
      );
  
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
  
      if (data.dl_url) {
        // Create audio object with metadata for the player
        const audioData = {
          url: data.dl_url,
          title: `Lucid_Audio: ${text.substring(0, 30)}`,
          type: 'generated',
          id: 'generated-' + Date.now()
        };

        // Pass the generated audio back to parent
        onAudioGenerated(audioData);
  
        await logEnhancedGeneration(
          user.uid,
          user.email,
          voice,
          text.length,
          text,
          data.dl_url,
          flowValue,
          paceValue,
          true // success flag
        );
  
        // Update quota display after successful generation
        if (setQuotaStatus) {
          const newStatus = await getQuotaStatus(user.uid);
          setQuotaStatus(newStatus);
        }
      }
    } catch (error) {
      console.error('Generation failed:', error);
      await logEnhancedGeneration(
        user.uid,
        user.email,
        voice,
        text.length,
        text,
        null, // no dl_url for failed attempts
        flowValue,
        paceValue,
        false // failed flag
      );
      alert('Failed to generate audio. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className="w-full flex flex-col gap-4 mb-[4rem] sm:mb-[4rem] md:mb-0">
      <div className="grid grid-cols-12 gap-[1rem]">
        {/* Left Column - Text Input & Controls */}
        <div className="col-span-12 lg:col-span-8 bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-[1.25rem] h-[clamp(500px,75vh,900px)] flex flex-col overflow-visible">
          <div className="flex items-center justify-between">
            {/* Language Selector on the Left */}
            <div className="w-80">
              <LanguageSelector />
            </div>

            {/* Tooltip on the Far Right */}
            <div className="flex items-center">
              <Tooltip
                text={[
                  "Expand short forms (abbreviations) into full words unless commonly spoken.",
                  "Convert numbers to words (e.g., 123 → నూటా ఇరవై మూడు).",
                  "Use commas (,) and double quotes (\" \") correctly. Avoid excessive dots (...).",
                  "Remove extra symbols, emojis 😊, and text inside brackets ( ) or [ ].",
                  "Add pauses (||0.5s||, ||1s||, ||2s||) in long sentences for natural speech."
                ]}
              />
            </div>
          </div>

          <div className="mt-[0.75rem] flex-grow">
            <TransliterationTextBox
              value={text}
              onChange={handleTextChange}
              placeholder="Start typing in English, and we'll convert it to Telugu for you!"
              className="w-full h-full p-[1rem] bg-[#181A1B] text-[#EAECEF] border border-[#3A3D3E]
                      rounded-lg focus:outline-none focus:ring-2 focus:ring-[#178A94]
                      text-[clamp(1rem,2vw,1.125rem)] resize-none placeholder-[#7C8085]
                      scrollbar-thin scrollbar-thumb-[#178A94] scrollbar-track-[#2C2F30]"
            />
          </div>
          {/* PostProcessingCheckbox removed */}
          <div className="mt-[0.75rem] grid grid-cols-2 gap-2">
            <div className="pr-1">
              <FlowAdjuster value={flowValue} onChange={(e) => setFlowValue(parseInt(e.target.value))} />
            </div>
            <div className="pl-1">
              <PaceAdjuster value={paceValue} onChange={(e) => setPaceValue(parseInt(e.target.value))} />
            </div>
          </div>
        </div>

        {/* Right Column - Voice Selector */}
        <div className="col-span-12 lg:col-span-4 bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-[1.25rem] h-[clamp(500px,75vh,900px)]
                    overflow-y-auto
                    scrollbar scrollbar-w-[0.5rem]
                    scrollbar-thumb-[#178A94] hover:scrollbar-thumb-[#146E75]
                    scrollbar-track-[#2C2F30]
                    scrollbar-thumb-rounded-full
                    scrollbar-track-rounded-full">
          <VoiceSelector
            voices={voices}
            selectedVoice={voice}
            onVoiceSelect={setVoice}
            onPreviewPlay={(voiceId, previewUrl, voiceName) => {
              // Create preview audio object
              const previewAudio = {
                url: previewUrl,
                title: `Preview: ${voiceName}`,
                type: 'preview',
                id: voiceId
              };
              onAudioGenerated(previewAudio);
            }}
            currentlyPlaying={null} // This will be managed by GlobalAudioPlayer
          />
        </div>
      </div>
    
      {/* Generate Button */}
      <div className="w-full">
        <button
          onClick={handleGenerate}
          disabled={!text || !voice || isGenerating}
          className="w-full p-[1.15rem] bg-[#178A94] text-[#EAECEF] rounded-lg uppercase font-bold
                  hover:bg-[#146E75] transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center text-[clamp(1rem,1.5vw,1.125rem)]
                  mb-[0.8rem] md:mb-[0.8rem]"
        >
          {isGenerating ? (
            <>
              <span className="animate-spin mr-[0.5rem]">⚡</span>
              Generating...
            </>
          ) : (
            <>
              <Wand2 className="mr-[0.5rem]" size={24} />
              Generate Audio
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GenerateSpeech;